import { useEffect } from 'react';
import ContactSection from '../../Components/ContactSection';
import '../../css/ForagePage.css';
import ForageCanvas from './ForageCanvas';
import ForageChart from './ForageChart';
import ForageControls from './ForageControls';
import ForageRunControls from './ForageRunControls';
import useForageSimulation from './useForageSimulation';

function formatCount(value) {
    return new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 }).format(value);
}

function ForagePage() {
    const {
        config,
        snapshot,
        paused,
        mode,
        evaluationSpeed,
        updateConfig,
        togglePaused,
        setMode,
        updateEvaluationSpeed,
        restartEvaluation,
        reset,
        newWorld,
    } = useForageSimulation();

    useEffect(() => {
        const previousTitle = document.title;
        document.title = 'Forage · Aditya Patel';
        return () => {
            document.title = previousTitle;
        };
    }, []);

    return (
        <div className="forage-page-shell">
            <main className="forage-page">
                <nav className="forage-nav">
                    <a href="/">Aditya Patel</a>
                    <span>Interactive experiment · Reinforcement learning</span>
                </nav>

                <header className="forage-intro">
                    <p className="forage-kicker">Forage</p>
                    <h1>Can a hungry agent learn where to look?</h1>
                    <p>
                        A small agent begins with no idea where food is or how to reach it. Change the
                        environment and its learning rules, then watch trial and error become a policy.
                    </p>
                </header>

                <section className="forage-experiment" aria-label="Forage simulation">
                    <div className="forage-mode-switch" role="tablist" aria-label="Experiment mode">
                        <button
                            type="button"
                            role="tab"
                            aria-selected={mode === 'learn'}
                            className={mode === 'learn' ? 'is-active' : ''}
                            onClick={() => setMode('learn')}
                        >
                            <span>01</span>
                            Learn
                        </button>
                        <button
                            type="button"
                            role="tab"
                            aria-selected={mode === 'watch'}
                            className={mode === 'watch' ? 'is-active' : ''}
                            onClick={() => setMode('watch')}
                        >
                            <span>02</span>
                            Watch one run
                        </button>
                    </div>

                    <div className="forage-lab">
                        {mode === 'learn' ? (
                            <>
                                <div className="forage-stage forage-training-stage">
                                    <div className="forage-training-copy">
                                        <p className="forage-kicker">Training</p>
                                        <h2>{paused ? 'Learning is paused.' : 'Learning without the theater.'}</h2>
                                        <p>
                                            The worker runs complete episodes as quickly as it can. Only measured
                                            outcomes appear here; the animated agent is reserved for a frozen run.
                                        </p>
                                    </div>
                                    <div className="forage-metrics">
                                        <div><span>Training steps</span><strong>{formatCount(snapshot.steps)}</strong></div>
                                        <div><span>Observations seen</span><strong>{snapshot.learnedStates} / {snapshot.totalStates}</strong></div>
                                        <div><span>Moves toward visible food</span><strong>{snapshot.goalDirected.toFixed(0)}%</strong></div>
                                        <div><span>Food / 100 moves</span><strong>{snapshot.evaluationScore.toFixed(1)}</strong></div>
                                    </div>
                                    <ForageChart
                                        history={snapshot.goalDirectedHistory}
                                        baseline={snapshot.randomPolicyBaseline}
                                    />
                                    <p className="forage-learning-note">
                                        The graph checks whether the preferred action for each learned, visible-food
                                        state moves the agent closer. The dotted line is random choice. Food rate is
                                        separately measured by freezing the policy and testing it on the same 32 worlds.
                                    </p>
                                </div>

                                <ForageControls
                                    config={config}
                                    paused={paused}
                                    onChange={updateConfig}
                                    onPause={togglePaused}
                                    onReset={reset}
                                    onNewWorld={newWorld}
                                />
                            </>
                        ) : (
                            <>
                                <div className="forage-stage">
                                    <div className="forage-metrics">
                                        <div><span>Run steps</span><strong>{snapshot.evaluation.steps}</strong></div>
                                        <div><span>Food found</span><strong>{snapshot.evaluation.foodCollected}</strong></div>
                                        <div><span>Run reward</span><strong>{snapshot.evaluation.reward.toFixed(1)}</strong></div>
                                        <div><span>Energy</span><strong>{Math.max(0, snapshot.evaluation.agent.energy).toFixed(0)}</strong></div>
                                    </div>
                                    <ForageCanvas
                                        snapshot={{
                                            gridSize: snapshot.gridSize,
                                            ...snapshot.evaluation,
                                        }}
                                        vision={config.vision}
                                    />
                                    <p className="forage-run-caption">
                                        The agent always chooses its highest-valued action. Random exploration and
                                        learning are disabled for the entire run.
                                    </p>
                                </div>

                                <ForageRunControls
                                    evaluation={snapshot.evaluation}
                                    paused={paused}
                                    speed={evaluationSpeed}
                                    onPause={togglePaused}
                                    onRestart={restartEvaluation}
                                    onSpeedChange={updateEvaluationSpeed}
                                />
                            </>
                        )}
                    </div>
                </section>

                <section className="forage-explanation">
                    <div>
                        <p className="forage-kicker">What it knows</p>
                        <h2>Only what falls inside the circle.</h2>
                    </div>
                    <div className="forage-explanation-copy">
                        <p>
                            The agent observes the relative direction of the nearest visible food and
                            chooses to move up, down, left, or right. Outside its vision, it has to explore.
                        </p>
                        <p>
                            What it learns is a table. For each relative food position—three cells left and one
                            cell up, for example—the Q-table stores four long-term action values. The neural
                            brain instead passes five observations through twelve hidden units to estimate the
                            same four values. At first its choices are effectively random. Training separates
                            the values until movement toward visible food becomes the preferred action.
                        </p>
                        <p>
                            Training runs faster than the animation in a separate browser worker. The agent
                            on screen is a slower replay of the policy being learned behind it.
                        </p>
                    </div>
                </section>

                <footer className="forage-footer">
                    <span>No backend. No model API.</span>
                    <span>Everything learns locally in this tab.</span>
                </footer>
            </main>
            <ContactSection />
        </div>
    );
}

export default ForagePage;
